import { Button, Container, Form, Header, Icon, Segment } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { RegieContext } from '@/contexts';
import { getRegisseurs, postConnexion } from '@/routes';
import type { ApiResponseType, RegisseurType } from '@/types';
import { useRegieStore, useUtilisateurStore } from '@/zustand';

type ConnexionApiType = {
  authenticated: boolean;
};

type RegisseurLoginProps = {
  forcedLogin?: boolean;
  onConnect: (regisseurs?: RegisseurType[]) => boolean;
};

const RegisseurLogin = (props: RegisseurLoginProps) => {
  const { forcedLogin, onConnect } = props;

  const [customLogin, setCustomLogin] = React.useState(false);
  const [userLogin, setUserLogin] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState({ identity: false, password: false, network: false });

  const utilisateur = useUtilisateurStore((state) => state.utilisateur);
  const { login } = utilisateur || {};

  const regie = useRegieStore((state) => state.regie);
  const { showList, regies } = React.useContext(RegieContext);

  const handlePasswordChange = React.useCallback(({ target: { value } }) => {
    setPassword(value);

    setError({ identity: false, password: false, network: false });
  }, []);

  const handleLoginChange = React.useCallback(({ target: { value } }) => {
    setUserLogin(value);

    setError({ identity: false, password: false, network: false });
  }, []);

  const handleLogin = React.useCallback(async () => {
    setLoading(true);

    try {
      const { authenticated } = await request.post<unknown, ConnexionApiType>(postConnexion(), {
        username: userLogin,
        password,
      });

      if (!authenticated) {
        setError({ ...error, identity: true, password: true });
      } else {
        // Si ok, on récupère le régisseur qui lui correspond
        const { data } = await request.get<unknown, ApiResponseType<RegisseurType>>(
          getRegisseurs({ page: 1, identifiantLogin: utilisateur?.profil.identifiant }),
        );

        if (!onConnect(data)) {
          setError({ ...error, identity: true });
        }
      }
    } catch (_authenticationError) {
      setError({ ...error, network: true });
    }

    setPassword('');
    setLoading(false);
  }, [error, onConnect, password, userLogin, utilisateur?.profil.identifiant]);

  React.useEffect(() => {
    if (login) {
      setUserLogin(login);
    }
  }, [login]);

  const passwordInput = React.useMemo(
    () => (
      <Form.Input
        input={{ 'data-testid': 'password-input' }}
        autoFocus
        icon={<Icon name="lock" />}
        iconPosition="left"
        placeholder="Mot de passe"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={handlePasswordChange}
        disabled={loading}
        error={error.identity || error.password}
        title={error.identity || error.password ? 'Identifiant ou mot de passe incorrect' : ''}
        required
        fluid
      />
    ),
    [error.identity, error.password, handlePasswordChange, loading, password],
  );

  const loginInput = React.useMemo(
    () => (
      <Form.Input
        input={{ 'data-testid': 'login-input' }}
        autoFocus
        onFocus={(e) => e.target.select()}
        icon={<Icon name="user" />}
        iconPosition="left"
        placeholder="Identifiant régisseur"
        value={userLogin}
        onChange={handleLoginChange}
        disabled={loading}
        error={error.identity || error.password}
        title={error.identity || error.password ? 'Identifiant ou mot de passe incorrect' : ''}
        required
        fluid
      />
    ),
    [error.identity, error.password, handleLoginChange, loading, userLogin],
  );

  const loginButton = React.useMemo(
    () => (
      <Form.Button
        data-testid="login-button"
        size="large"
        type="submit"
        labelPosition="left"
        disabled={_.isEmpty(password) || error.identity || error.password || loading}
        loading={loading}
        icon
        fluid
        className="login-button"
        onClick={handleLogin}
      >
        {/* @ts-expect-error Pour une raison que j'ignore, c'est pas typescripté mais ça existe */}
        <Icon name="log in" />
        Accéder à la régie
      </Form.Button>
    ),
    [error.identity, error.password, handleLogin, loading, password],
  );

  return (
    <Container id="regisseur-login" fluid style={{ paddingTop: 100, alignItems: 'center' }}>
      <Segment basic textAlign="center">
        <Header as="h1" icon>
          <Icon.Group size="large" className="colored">
            <Icon name="id badge" />
            <Icon
              name="euro"
              corner="bottom right"
              style={{ fontSize: '1em', top: '1.75em', left: '1em' }}
            />
          </Icon.Group>
          <Header.Content>
            <span
              style={{
                position: 'relative',
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                ...(_.size(regies) > 1 && {
                  cursor: 'pointer',
                }),
              }}
              {...(_.size(regies) > 1 && {
                onClick: showList,
              })}
            >
              <span>{regie?.titre}</span>
              {_.size(regies) > 1 && (
                <Button
                  circular
                  icon="exchange"
                  size="tiny"
                  className="domain-colored"
                  style={{
                    position: 'absolute',
                    right: -65,
                    padding: 10,
                    marginTop: 10,
                    fontSize: '0.2em',
                  }}
                  title="Changer de régie"
                  data-testid="switch-regie-button"
                />
              )}
            </span>
            {regie?.ecole && (
              <Header.Subheader
                style={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  fontStyle: 'italic',
                  marginTop: 5,
                }}
              >
                <span
                  className="current-color"
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Régie test
                  <Icon
                    name="FlaskConical"
                    className="colored"
                    iconSet="Lucide"
                    size={22}
                    style={{ position: 'absolute', right: -30 }}
                  />
                </span>
                <br />
              </Header.Subheader>
            )}
            <Header.Subheader style={{ marginTop: 15 }}>
              Veuillez vous authentifier pour accéder à la régie
              <br />
            </Header.Subheader>
          </Header.Content>
        </Header>
        {!forcedLogin && !customLogin && (
          <Segment basic attached="top" style={{ padding: 0 }}>
            {login && (
              <Header
                as="h5"
                disabled
                style={{ display: 'inline' }}
              >{`Identifiant : ${login}`}</Header>
            )}
            <Icon
              link
              name="pencil"
              style={{
                position: 'absolute',
                marginLeft: '0.5em',
                marginTop: 2,
              }}
              title="Changer d'utilisateur"
              onClick={() => setCustomLogin(true)}
              data-testid="edit-login-button"
            />
          </Segment>
        )}
        <Segment basic attached="bottom" style={{ display: 'flex', justifyContent: 'center' }}>
          <Form
            // @ts-expect-error
            style={{ width: 300, marginTop: 10 }}
          >
            {customLogin && loginInput}
            {passwordInput}
            {loginButton}
          </Form>
        </Segment>
      </Segment>
    </Container>
  );
};

export default RegisseurLogin;
